/* DON'T EDIT THIS FILE: edit original and run build again */ import { isPlainObject } from "../../framework/core/is-plain-object.ts";
import { toArray } from "../../framework/core/maybe-array.ts";
import {
  typeCheckerError,
  typeCheckerOkResult,
  typeCheckerSchemaFailResult,
} from "./type-checker-errors.ts";
import { typeCheckerResolveResult } from "./type-checker-resolve-result.ts";
import {
  TypeCheckerError,
  TypeCheckerErrorType,
  TypeCheckerRecordSchema,
  TypeCheckerRecurseChecker,
  TypeCheckerResult,
  TypeCheckerStatus,
  WithAnyTypeCheckerSchemaOptions,
} from "./type-checker-types.ts";

type TypeCheckerRecordSubResult = {
  skip: boolean;
  result: TypeCheckerResult;
};

const typeCheckKeyOrValue = (
  checker: WithAnyTypeCheckerSchemaOptions<TypeCheckerRecordSchema>,
  type: "key" | "value",
  subValue: unknown,
  subPath: string,
  check: TypeCheckerRecurseChecker
): TypeCheckerRecordSubResult => {
  const schema = type === "key" ? checker.keysSchema : checker.valuesSchema;
  let subResult: TypeCheckerResult = typeCheckerSchemaFailResult(
    subValue,
    subPath
  );
  for (const orSchema of toArray(schema)) {
    subResult = check(orSchema, subValue, subPath);
    if (subResult.status === TypeCheckerStatus.ok) {
      return { skip: false, result: subResult };
    }
  }
  if (checker.skipInvalid) {
    return { skip: true, result: subResult };
  }
  return {
    skip: false,
    result: typeCheckerResolveResult(checker, subValue, subPath, subResult),
  };
};

export const typeCheckerCheckRecord = (
  checker: WithAnyTypeCheckerSchemaOptions<TypeCheckerRecordSchema>,
  value: unknown,
  path: string,
  check: TypeCheckerRecurseChecker
): TypeCheckerResult => {
  if (!isPlainObject(value)) {
    return typeCheckerResolveResult(
      checker,
      value,
      path,
      typeCheckerSchemaFailResult(value, path)
    );
  }
  const validated: any = {};
  const errors: TypeCheckerError[] = [];
  for (const [key, keyValue] of Object.entries(value)) {
    const subPath = path + "." + key;
    const { skip: skipKey, result: keyResult } = typeCheckKeyOrValue(
      checker,
      "key",
      key,
      subPath,
      check
    );
    if (skipKey) {
      errors.push(
        typeCheckerError(TypeCheckerErrorType.skippedInvalid, { key }, subPath)
      );
      continue;
    } else if (keyResult.status !== TypeCheckerStatus.ok) {
      return keyResult;
    }
    const { skip: skipValue, result: valueResult } = typeCheckKeyOrValue(
      checker,
      "value",
      keyValue,
      subPath,
      check
    );
    if (skipValue) {
      errors.push(
        typeCheckerError(
          TypeCheckerErrorType.skippedInvalid,
          { value: keyValue },
          subPath
        )
      );
      continue;
    } else if (valueResult.status !== TypeCheckerStatus.ok) {
      return valueResult;
    }
    validated[keyResult.value as string] = valueResult.value;
    errors.push(...keyResult.errors, ...valueResult.errors);
  }
  return typeCheckerOkResult(validated, errors);
};
